import { FC, HTMLAttributes, useEffect } from 'react';

import { useRecoilState, useSetRecoilState } from 'recoil';

import { BottomPopup } from '@shared/components/BottomPopup';
import { Icon } from '@shared/components/icons';
import { simpleOpenAtomFamily } from '@shared/state/atom';
import { FeventDate } from '@shared/types';
import { formatDate } from '@shared/utils/formatDate';
import { fromKstParts, kstStartOfDay, toKstParts } from '@shared/utils/kstDate';
import { UseFormSetValue } from 'react-hook-form';
import { useId } from 'react-id-generator';

import TimePickerModal from './TimePickerModal';
import { feventTimeAtomFamily } from './time.atom';

interface Props extends HTMLAttributes<HTMLDivElement> {
  name: string;
  fDate: FeventDate;
  setValue: UseFormSetValue<any>;
  pickerId: string;
}

export const FeventTimePicker: FC<Props> = ({
  name,
  fDate,
  setValue,
  placeholder,
  pickerId,
  className = '',
  ...props
}: Props) => {
  const { TimePlaceholder, Time, X } = Icon();

  //* fDate가 있고, timeDefined true일때 초기값을 세팅함(editPage 전용), 없다면 null
  //* 이 time이 feventTimePikcer의 시간 저장 값
  //! DatePicker에서 date를 날려도 이녀석은 저장하고 있음
  const [time, setTime] = useRecoilState(feventTimeAtomFamily(pickerId));

  useEffect(() => {
    if (fDate && fDate.timeDefined && fDate.date) {
      setTime(fDate.date);
    }
  }, [fDate, setTime]);

  const [id] = useId(1, 'timepicker');

  const openId = `timepicker-${id}`;
  const setOpen = useSetRecoilState(simpleOpenAtomFamily(openId));

  //* 시간만 지운다 — 날짜는 남기고 시각은 KST 자정으로 되돌림(날짜 선택 직후와 같은 모양)
  const onClear = () => {
    setTime(null);
    setValue(name, {
      date: fDate?.date ? kstStartOfDay(fDate.date) : null,
      timeDefined: false,
    });
  };

  //* timePickerModal time 선택시 함수
  //* 고른 시·분을 한국 시간(KST) 기준으로 날짜에 합성한다(해외 입력 시 시각 밀림 방지).
  const onTimeChange = (timeDate: Date) => {
    if (!fDate.date) return;
    const d = toKstParts(fDate.date);
    const t = toKstParts(timeDate);
    const tempDate = fromKstParts(d.year, d.month, d.date, t.hours, t.minutes);
    setTime(tempDate);
    setValue(name, {
      date: tempDate,
      timeDefined: true,
    });
  };

  return (
    <>
      <div
        className={`date-picker flex cursor-pointer justify-between px-1 theme-bg-1 theme-border-1 ${className}`}
        onClick={() => {
          //* date가 없다면 변경
          if (fDate?.date) {
            setOpen(true);
          } else {
            alert(`${placeholder?.split('시간')[0]}일을 먼저 선택해주세요`);
          }
        }}
        {...props}
      >
        <div className="flex min-w-0 flex-1 items-center">
          {time ? (
            <Time className="h-6 w-6 shrink-0 stroke-none" />
          ) : (
            <TimePlaceholder className="h-6 w-6 shrink-0 stroke-none" />
          )}
          <p className={`whitespace-nowrap ${!time ? 'theme-text-6' : 'theme-text-1'}`}>
            {time
              ? formatDate(time, {
                  timeSeparater: ' : ',
                  contains: {
                    year: false,
                    month: false,
                    date: false,
                    amPm: true,
                    hours: true,
                    minutes: true,
                  },
                })
              : placeholder}
          </p>
        </div>
        {time && (
          <X
            onClick={(e) => {
              e.stopPropagation();
              //* 실수로 눌러 지워지지 않게 확인 후 삭제
              if (!window.confirm(`${placeholder}을 지울까요?`)) return;
              onClear();
            }}
            className="h-5 w-5 shrink-0 stroke-slate-400"
          />
        )}
      </div>
      <BottomPopup openId={openId}>
        <div className="w-screen bg-transparent">
          <TimePickerModal
            time={time}
            onTimeChange={onTimeChange}
            openId={openId}
            placeholder={placeholder}
          />
        </div>
      </BottomPopup>
    </>
  );
};
